import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { DateTime } from "luxon";
import DataBase from "../../dataBase/liquidGuides";
import Guide from "../../Models/Guide";
import { slice1Actions } from "../../store/reducer";
import "./TripAI.css";
import "./AdminDashboard.css";
import UsageLimitsPanel from "./UsageLimitsPanel";
import GuideReportsPanel from "./GuideReportsPanel";
import FeaturedMainSchedulePanel from "./FeaturedMainSchedulePanel";
import FeaturedWikiFixPanel from "./FeaturedWikiFixPanel";
import FeaturedGuideFixPanel from "./FeaturedGuideFixPanel";
import TravelerGuidePanel from "./TravelerGuidePanel";
import { buildTaggedRows } from "../../Common/taggedLinks";

// Web-only DIY analytics dashboard — reads straight from Firestore
// (users/guide_info/analytics_events) instead of a dedicated analytics
// SDK, since native Firebase Analytics on mobile would need a new
// app-store build cycle. Reached only via the hidden "admin" Command
// Palette trigger (MobileBottomNavBar.js) once window.admin_unlocked is
// set — not real access control, the data behind it just isn't sensitive.
// Fetch-all-and-aggregate-client-side; fine at this app's scale, revisit
// with precomputed counters only if it gets slow. See memory: DIY
// Firestore analytics plan.
const PERIOD_OPTIONS = [
  { label: "7 days", days: 7 },
  { label: "30 days", days: 30 },
  { label: "90 days", days: 90 },
  { label: "6 months", days: 182 },
  { label: "1 year", days: 365 },
];

const dayKey = (isoOrDate) => {
  const dt = typeof isoOrDate === "string" ? DateTime.fromISO(isoOrDate) : DateTime.fromJSDate(isoOrDate);
  return dt.isValid ? dt.toISODate() : null;
};

const bucketByDay = (items, getDateValue) => {
  const counts = {};
  items.forEach((item) => {
    const raw = getDateValue(item);
    if (!raw) return;
    const key = dayKey(raw);
    if (!key) return;
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
};

// Each event chart shows web, iOS and Android stacked in one bar, with the
// totals in the legend — one chart instead of a platform filter to flip.
const PLATFORMS = [
  { key: "web", label: "Web", color: "#008cba" },
  { key: "ios", label: "iOS", color: "#8e8e93" },
  { key: "android", label: "Android", color: "#34a853" },
];
const bucketByPlatform = (items) => {
  const out = {};
  PLATFORMS.forEach((p) => { out[p.key] = bucketByDay(items.filter((e) => e.platform === p.key), (e) => e.ts?.toDate?.()); });
  return out;
};
const TABS = [
  { key: "activity", label: "Activity" },
  { key: "marketing", label: "Marketing" },
  { key: "guides", label: "Guides" },
  { key: "settings", label: "Settings" },
  { key: "limits", label: "Usage & limits" },
  { key: "reports", label: "Reports" },
];

// One bar per day is fine up to ~60 days; at 6 months/1 year that's 180-365
// razor-thin bars, unreadable. Roll longer ranges up into weekly (<=200
// days) or monthly (>200 days) buckets instead. Three kinds of data:
//   counts      — one series, keyed by ISO day string;
//   byPlatform  — { web, ios, android } of such counts, drawn stacked;
//   cumulative  — the running total of `sortedDates` (a sorted array of ISO
//                 day strings) as of each bucket's end: a guide created in an
//                 earlier, unselected period still counts toward every later
//                 bucket's total, not just the day it happened.
const BarChart = ({ title, counts, byPlatform, sortedDates, days, cumulative }) => {
  const today = DateTime.now().startOf("day");
  // Binary search for "how many sortedDates are <= isoDay" — sortedDates is
  // small enough (guide count) that this is just for clarity, not speed.
  const countAsOf = (isoDay) => {
    if (!sortedDates) return 0;
    let lo = 0, hi = sortedDates.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (sortedDates[mid] <= isoDay) lo = mid + 1; else hi = mid;
    }
    return lo;
  };
  // Buckets first (which days each bar covers), then the numbers.
  const buckets = [];
  if (days <= 60) {
    for (let i = days - 1; i >= 0; i--) {
      const d = today.minus({ days: i });
      buckets.push({ key: d.toISODate(), label: d.toFormat("MMM d"), dayKeys: [d.toISODate()] });
    }
  } else {
    const byMonth = days > 200;
    const bucketCount = byMonth ? Math.ceil(days / 30) : Math.ceil(days / 7);
    for (let i = bucketCount - 1; i >= 0; i--) {
      const bucketEnd = byMonth ? today.minus({ months: i }) : today.minus({ weeks: i });
      const bucketStart = byMonth ? bucketEnd.minus({ months: 1 }).plus({ days: 1 }) : bucketEnd.minus({ weeks: 1 }).plus({ days: 1 });
      const dayKeys = [];
      for (let cursor = bucketStart; cursor <= bucketEnd; cursor = cursor.plus({ days: 1 })) dayKeys.push(cursor.toISODate());
      buckets.push({ key: bucketEnd.toISODate(), label: byMonth ? bucketEnd.toFormat("MMM yyyy") : bucketEnd.toFormat("MMM d"), dayKeys });
    }
  }
  const sumOf = (c, dayKeys) => dayKeys.reduce((n, k) => n + ((c && c[k]) || 0), 0);
  const series = buckets.map((b) => {
    if (byPlatform) {
      const parts = PLATFORMS.map((p) => ({ ...p, value: sumOf(byPlatform[p.key], b.dayKeys) }));
      return { ...b, parts, value: parts.reduce((n, x) => n + x.value, 0) };
    }
    return { ...b, value: cumulative ? countAsOf(b.key) : sumOf(counts, b.dayKeys) };
  });
  const max = Math.max(1, ...series.map((x) => x.value));
  const total = cumulative ? (series[series.length - 1]?.value || 0) : series.reduce((n, x) => n + x.value, 0);
  const platformTotals = byPlatform ? PLATFORMS.map((p) => ({ ...p, value: series.reduce((n, x) => n + x.parts.find((y) => y.key === p.key).value, 0) })) : null;
  return (
    <div className="admindash-chart">
      <div className="admindash-chart-title">
        {title} <span className="admindash-chart-total">{total}</span>
        {platformTotals && (
          <span className="admindash-legend">
            {platformTotals.map((p) => (
              <span className="admindash-legend-item" key={p.key}>
                <span className="admindash-legend-dot" style={{ background: p.color }} />
                {p.label} {p.value}
              </span>
            ))}
          </span>
        )}
      </div>
      <div className="admindash-bars">
        {series.map((x) => (
          <div
            className="admindash-bar-col"
            key={x.key}
            title={x.parts ? `${x.label}: ${x.value} (${x.parts.map((p) => `${p.label} ${p.value}`).join(", ")})` : `${x.label}: ${x.value}`}
          >
            {x.parts ? (
              <div className="admindash-bar-stack" style={{ height: `${(x.value / max) * 100}%` }}>
                {x.parts.filter((p) => p.value > 0).map((p) => (
                  <div key={p.key} style={{ flex: p.value, background: p.color }} />
                ))}
              </div>
            ) : (
              <div className="admindash-bar" style={{ height: `${(x.value / max) * 100}%` }} />
            )}
          </div>
        ))}
      </div>
      <div className="admindash-chart-footer">
        <span>{series[0]?.label}</span>
        <span>{series[series.length - 1]?.label}</span>
      </div>
    </div>
  );
};

// Saves rows (arrays of cells; the first row is the header) as a CSV file
// that opens cleanly in Excel / Numbers / Sheets.
const downloadCsv = (fileName, rows) => {
  const cell = (v) => {
    const t = v == null ? "" : String(v);
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const blob = new Blob(["\ufeff" + rows.map((r) => r.map(cell).join(",")).join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
};

const AdminDashboard = ({ onHide, currentUid }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [users, setUsers] = useState([]);
  const [guides, setGuides] = useState([]);
  const [events, setEvents] = useState([]);
  const [periodDays, setPeriodDays] = useState(30);
  const [tab, setTab] = useState("activity");
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);

  const [lookupId, setLookupId] = useState("");
  const [lookupResult, setLookupResult] = useState(null); // { infoDoc, guide_info_id } | null
  const [lookupError, setLookupError] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [featuredBusy, setFeaturedBusy] = useState(false);

  const [includeOwnGuides, setIncludeOwnGuides] = useState(false);
  const [viewsFeaturedOnly, setViewsFeaturedOnly] = useState(true);
  const [viewsShowAll, setViewsShowAll] = useState(false);
  const [includeAutomated, setIncludeAutomated] = useState(false);

  const [announceText, setAnnounceText] = useState("");
  // Empty array = show to everyone (matches original, pre-targeting
  // behavior) — not "show to nobody".
  const [announcePlatforms, setAnnouncePlatforms] = useState([]);
  // Restricts the announcement to dev/test builds only, so it can be
  // previewed without publishing to real users at all.
  const [announceTestOnly, setAnnounceTestOnly] = useState(false);
  const [announceSaving, setAnnounceSaving] = useState(false);
  const [announceMsg, setAnnounceMsg] = useState("");

  const [minVersionIos, setMinVersionIos] = useState("");
  const [minVersionAndroid, setMinVersionAndroid] = useState("");
  const [minVersionWeb, setMinVersionWeb] = useState("");
  const [minVersionSaving, setMinVersionSaving] = useState(false);
  const [minVersionMsg, setMinVersionMsg] = useState("");

  // Refresh re-reads users, guides and events. Settings (announcement,
  // minimum versions) are read once only, so a refresh never throws away
  // text being typed on the Settings tab.
  useEffect(() => {
    DataBase.getAllUsersAndGuides((res) => {
      if (res.pass) {
        setUsers(res.users);
        setGuides(res.guides);
      } else {
        setError("Failed to load users/guides: " + (res.error?.message || res.error));
      }
      setLoading(false);
    });
  }, [refreshKey]);

  useEffect(() => {
    DataBase.readSettings((res) => {
      if (res.pass) {
        setAnnounceText(res.res.announce || "");
        setAnnouncePlatforms(res.res.announce_platforms || []);
        setAnnounceTestOnly(!!res.res.announce_test_only);
        const versions = res.res.min_supported_version || {};
        setMinVersionIos(versions.ios || "");
        setMinVersionAndroid(versions.android || "");
        setMinVersionWeb(versions.web || "");
      }
    });
  }, []);

  useEffect(() => {
    const since = DateTime.now().minus({ days: periodDays }).toJSDate();
    setRefreshing(true);
    DataBase.getAnalyticsEvents(since, (res) => {
      if (res.pass) {
        setEvents(res.events);
        setError("");
        setLoadedAt(DateTime.now());
      } else setError("Failed to load events: " + (res.error?.message || res.error));
      setRefreshing(false);
    });
  }, [periodDays, refreshKey]);

  const totalUsers = users.length;
  const totalGuides = guides.length;
  const totalShared = guides.filter((g) => Array.isArray(g.access) && g.access.length > 1).length;

  // Recently modified guides — a <select> instead of a text field so it's
  // immune to whatever was blocking keyboard input in the lookup box above;
  // picking one behaves exactly like typing its id in manually. `guides`
  // entries carry `id` (the guide_info doc id, via getAllUsersAndGuides).
  // Excludes the admin's own guides by default — this list is meant to
  // surface real user activity to check up on, not the admin's own
  // testing/edits — but includeOwnGuides lets that be toggled on, e.g. to
  // verify the exclusion itself is working correctly.
  const recentGuides = [...guides]
    .filter((g) => includeOwnGuides || g.user_id !== currentUid)
    .sort((a, b) => (b.mod || "").localeCompare(a.mod || ""))
    .slice(0, 30);

  const usersByDay = bucketByDay(users, (u) => u.create_date);
  // Sorted (not bucketed) creation dates — feeds BarChart's cumulative mode
  // for "Total Guides over time" (a running total is more useful here than
  // per-period new-guide counts, which mostly read near-zero except on
  // whatever days testing/bulk creation happened to occur).
  const sortedGuideCreatedDates = guides.map((g) => dayKey(g.created)).filter(Boolean).sort();
  // Only analytics_events carry a platform (Platform.OS on mobile, "web" on
  // web) — users/guide_info don't, since a user or guide isn't tied to one
  // device. So the event charts are stacked by platform; the others aren't.
  // Web visitor events count only when a person was seen on the page (h: 1,
  // see logWhenHuman in liquidGuides.js) — the first day's web visits were
  // all Meta's automated link checkers, and are left out this way too. The
  // apps have no such problem, so their events count as they are.
  const VISITOR_EVENTS = ["visit", "guide_view", "engage"];
  // Possibly automated (2026-10-14): Google's link scanner passed the "a
  // person was seen" test by moving a simulated mouse. So a web visitor on a
  // computer whose only sign of life was mouse movement — no click, scroll,
  // key or "interacted" — is set aside unless the switch includes them.
  // Web visitor events from before that change carry no `sig` and can't be
  // told apart; they were tests and scanners, so they are left out too.
  const engagedVids = new Set(events.filter((e) => e.type === "engage" && e.vid).map((e) => e.vid));
  const isWebVisitorEvent = (e) => VISITOR_EVENTS.includes(e.type) && e.platform === "web";
  const looksAutomated = (e) => !e.sig || (e.dev === "desktop" && e.sig === "mousemove" && !engagedVids.has(e.vid));
  const possiblyAutomatedVids = new Set(events.filter((e) => isWebVisitorEvent(e) && e.h === 1 && e.type === "visit" && looksAutomated(e)).map((e) => e.vid));
  const platformEvents = events.filter((e) => !isWebVisitorEvent(e) || (e.h === 1 && (includeAutomated || !looksAutomated(e))));
  const ofType = (type) => platformEvents.filter((e) => e.type === type);
  const readsByPlatform = bucketByPlatform(ofType("guide_read"));
  const writesByPlatform = bucketByPlatform(ofType("guide_write"));
  const loginsByPlatform = bucketByPlatform(ofType("login"));
  const signupsByPlatform = bucketByPlatform(ofType("signup"));

  // First-visit funnel (web): of the browsers that arrived for the first
  // time in the period, how many opened a guide, interacted, made an
  // account, and saved a trip of their own. Steps are matched by the
  // browser's visitor id; the last one by the new account's uid.
  const newVisitorIds = new Set(ofType("visit").filter((e) => e.new === true && e.vid).map((e) => e.vid));
  // Returning visitors: browsers seen before that came back in the period
  // (not one of the period's new visitors), and what they did this time.
  const returningIds = new Set(ofType("visit").filter((e) => e.new !== true && e.vid && !newVisitorIds.has(e.vid)).map((e) => e.vid));
  const returningVisitors = returningIds.size;
  const returningWith = (type) => new Set(ofType(type).filter((e) => e.vid && returningIds.has(e.vid)).map((e) => e.vid)).size;
  const returningOpened = returningWith("guide_view");
  const returningInteracted = returningWith("engage");
  const vidsWith = (type) => new Set(ofType(type).filter((e) => e.vid && newVisitorIds.has(e.vid)).map((e) => e.vid));
  const newVisitorSignups = ofType("signup").filter((e) => e.vid && newVisitorIds.has(e.vid));
  const newSignupUids = new Set(newVisitorSignups.map((e) => e.uid));
  const savedOwnTrip = new Set(events.filter((e) => (e.type === "guide_write" || e.type === "guide_build_from") && newSignupUids.has(e.uid)).map((e) => e.uid)).size;
  const funnel = [
    { label: "New visitors", n: newVisitorIds.size },
    { label: "Opened a guide", n: vidsWith("guide_view").size },
    { label: "Interacted", n: vidsWith("engage").size },
    { label: "Made an account", n: new Set(newVisitorSignups.map((e) => e.vid)).size },
    { label: "Saved a trip of their own", n: savedOwnTrip },
  ];
  // What first brought each new account in. A link tag wins; then a guide
  // link with no tag (a video's QR code or a shared link); then the site the
  // browser came from; else it simply typed the address or used a bookmark.
  const signupSourceLabel = (e) => {
    if (e.platform === "ios") return "iOS app";
    if (e.platform === "android") return "Android app";
    if (e.first_src) return `Tagged link: ${e.first_src}`;
    if (e.first_guide) return "Guide link or QR code";
    if (e.first_ref) return `From ${e.first_ref}`;
    return "Direct (typed, bookmark or unknown)";
  };
  const tally = (items, keyOf) => {
    const m = new Map();
    items.forEach((e) => { const k = keyOf(e); if (k) m.set(k, (m.get(k) || 0) + 1); });
    return [...m.entries()].sort((a, b) => b[1] - a[1]);
  };
  // Tagged links (?src=…), one row per code: how many visits it brought, how
  // many of those were new visitors, the guide views that arrived on a
  // tagged guide link, and the accounts first brought in by that code.
  const taggedRows = buildTaggedRows(platformEvents);
  const signupSources = tally(ofType("signup"), signupSourceLabel);

  // CSV exports — the period's raw events (everything recorded, including
  // web visits not counted as people, flagged in the "counted" column), and
  // the two Marketing tables as shown.
  const stamp = `${periodDays}d-${DateTime.now().toFormat("yyyy-LL-dd")}`;
  const exportEvents = () => {
    const cols = ["type", "platform", "uid", "vid", "guide_id", "action", "source", "entry", "src", "ref", "new", "guest", "featured", "first_guide", "first_src", "first_ref", "days_since_first"];
    const counted = new Set(platformEvents);
    const rows = [...events]
      .sort((a, b) => (a.ts?.toMillis?.() || 0) - (b.ts?.toMillis?.() || 0))
      .map((e) => [e.ts?.toDate?.()?.toISOString() || "", ...cols.map((c) => e[c]), e.guide_id ? guideNameById.get(e.guide_id) || "" : "", counted.has(e) ? "yes" : "no"]);
    downloadCsv(`andiamo-events-${stamp}.csv`, [["time_utc", ...cols, "guide_name", "counted"], ...rows]);
  };
  const exportGuideViews = () =>
    downloadCsv(`andiamo-guide-views-${stamp}.csv`, [
      ["guide", "guide_id", "views", "by_link", "guests", "copied", "built_from", "login_reads"],
      ...viewRows.map((r) => [guideNameById.get(r.id) || "", r.id, r.views, r.byLink, r.guests, r.copies, r.builds, guideTimesRead.get(r.id) ?? ""]),
    ]);
  const exportTaggedLinks = () =>
    downloadCsv(`andiamo-tagged-links-${stamp}.csv`, [
      ["code", "visits", "new_visitors", "guide_views", "new_accounts", "guides_made", "accounts_that_made_a_guide"],
      ...taggedRows.map((r) => [r.src, r.visits, r.newVisitors, r.views, r.signups, r.guidesMade, r.makers]),
    ]);
  const signupFirstGuides = tally(ofType("signup"), (e) => e.first_guide).slice(0, 10);
  // Guide views (logGuideView in liquidGuides.js): everyone who opened a
  // guide in the period, signed in or not, the guide's own owner left out.
  // One row per guide, with what the view led to — a copy of the guide, or a
  // trip built from it. "By link" = opened on the guide's own link, which is
  // what a video's QR code does, so for a Featured guide that column is the
  // closest thing to "people the video brought in".
  const guideNameById = new Map(guides.map((g) => [g.id, g.guide_name || g.id]));
  const guideTimesRead = new Map(guides.map((g) => [g.id, Number(g.times_read) || 0]));
  const viewRows = (() => {
    const rows = new Map();
    const row = (id) => {
      if (!rows.has(id)) rows.set(id, { id, views: 0, guests: 0, byLink: 0, copies: 0, builds: 0, featured: false });
      return rows.get(id);
    };
    platformEvents.forEach((e) => {
      if (e.type === "guide_view" && e.guide_id) {
        const r = row(e.guide_id);
        r.views++;
        if (e.guest) r.guests++;
        if (e.entry === "link") r.byLink++;
        if (e.featured) r.featured = true;
      } else if (e.type === "guide_write" && e.action === "copy" && e.source) row(e.source).copies++;
      else if (e.type === "guide_build_from" && e.guide_id) row(e.guide_id).builds++;
    });
    return [...rows.values()]
      .filter((r) => !viewsFeaturedOnly || r.featured || guides.find((g) => g.id === r.id)?.type === "Featured")
      .sort((a, b) => b.views - a.views || b.byLink - a.byLink || b.copies + b.builds - (a.copies + a.builds));
  })();
  // The chart follows the same "Featured guides only" choice as the table,
  // so its total always matches the table's first row.
  const shownViewIds = new Set(viewRows.map((r) => r.id));
  const viewsByPlatform = bucketByPlatform(ofType("guide_view").filter((e) => shownViewIds.has(e.guide_id)));
  const viewTotals = viewRows.reduce((t, r) => ({ views: t.views + r.views, guests: t.guests + r.guests, byLink: t.byLink + r.byLink, copies: t.copies + r.copies, builds: t.builds + r.builds }),
    { views: 0, guests: 0, byLink: 0, copies: 0, builds: 0 });
  // Distinct accounts that logged in during the selected period/platform —
  // "Logins over time" counts every login event, so a handful of people
  // signing in repeatedly can look like heavy usage; this is the actual
  // reach number.
  const uniqueLogins = new Set(ofType("login").map((e) => e.uid)).size;

  // Distinct signed-in accounts with any analytics event per UTC day (all
  // platforms) — lets the Usage & limits panel show paid calls per active
  // user, so usage growth can be compared against user growth.
  const activeUsersByDay = {};
  events.forEach((e) => {
    const d = e.ts?.toDate?.();
    if (!d || !e.uid || e.uid === "guest") return;
    const key = d.toISOString().slice(0, 10);
    (activeUsersByDay[key] = activeUsersByDay[key] || new Set()).add(e.uid);
  });

  const handleLookup = (idOverride) => {
    const id = (idOverride ?? lookupId).trim();
    if (!id) return;
    setLookupLoading(true);
    setLookupError("");
    setLookupResult(null);
    // uid: "guest" — same convention readGuideTransaction already uses to
    // skip incrementing times_read / logging a real guide_read event, so an
    // admin peeking at a guide here doesn't pollute the real read stats.
    DataBase.readGuideTransaction(id, (res) => {
      setLookupLoading(false);
      if (res.pass) setLookupResult({ infoDoc: res.infoDoc, guide_info_id: res.guide_info_id, raw: res });
      else setLookupError("Guide not found.");
    }, "guest", { peek: true });
  };

  const handleSelectRecentGuide = (id) => {
    if (!id) return;
    setLookupId(id);
    handleLookup(id);
  };

  // Actually opens the guide in the main app (places/itinerary/photos —
  // not just the name/owner/Featured summary shown here), the same way
  // clicking a normal guide from a list does. Reuses the readGuideTransaction
  // result already fetched by handleLookup above rather than re-fetching.
  const handleOpenGuide = () => {
    if (!lookupResult?.raw?.pass) return;
    const res = lookupResult.raw;
    const GuideObj = new Guide(res.guide_id);
    const guide_json = JSON.parse(res.res.data);
    const decoded = GuideObj.decodeGuideFb(guide_json, res.infoDoc);
    if (decoded !== true) {
      setLookupError("Failed to open this guide (decode error).");
      return;
    }
    GuideObj.schedule.getPlaces_InSchedule();
    window.guide_obj = GuideObj;
    dispatch(slice1Actions.setGuide({ guide: "new" }));
    onHide();
  };

  const handleToggleFeatured = () => {
    if (!lookupResult) return;
    const nextFeatured = lookupResult.infoDoc.type !== "Featured";
    setFeaturedBusy(true);
    DataBase.setGuideFeatured(lookupResult.guide_info_id, nextFeatured, (res) => {
      setFeaturedBusy(false);
      if (res.pass) {
        setLookupResult((prev) => ({
          ...prev,
          infoDoc: { ...prev.infoDoc, type: nextFeatured ? "Featured" : "" },
        }));
      } else {
        setLookupError("Could not update Featured status.");
      }
    });
  };

  const toggleAnnouncePlatform = (platform) => {
    setAnnouncePlatforms((prev) =>
      prev.includes(platform) ? prev.filter((p) => p !== platform) : [...prev, platform]
    );
  };

  const handleSaveAnnounce = () => {
    setAnnounceSaving(true);
    setAnnounceMsg("");
    DataBase.updateAnnounce(announceText, announcePlatforms, announceTestOnly, (res) => {
      setAnnounceSaving(false);
      setAnnounceMsg(res.pass ? "Saved." : "Failed to save.");
    });
  };

  const handleClearAnnounce = () => {
    setAnnounceText("");
    setAnnouncePlatforms([]);
    setAnnounceTestOnly(false);
    setAnnounceSaving(true);
    setAnnounceMsg("");
    DataBase.updateAnnounce("", [], false, (res) => {
      setAnnounceSaving(false);
      setAnnounceMsg(res.pass ? "Cleared." : "Failed to clear.");
    });
  };

  // isVersionBelow (mobile's MainScreen.js / web's Common.js) only
  // understands dotted numeric strings ("1.2.3") — reject anything else
  // here rather than silently saving a value a client's comparison can't
  // use.
  const validVersion = (v) => v.trim() === "" || /^\d+(\.\d+){0,2}$/.test(v.trim());

  const handleSaveMinVersion = () => {
    if (!validVersion(minVersionIos) || !validVersion(minVersionAndroid) || !validVersion(minVersionWeb)) {
      setMinVersionMsg("Each version must be blank or a dotted version number, e.g. 2.4.0.");
      return;
    }
    setMinVersionSaving(true);
    setMinVersionMsg("");
    DataBase.updateMinSupportedVersion(
      { ios: minVersionIos.trim(), android: minVersionAndroid.trim(), web: minVersionWeb.trim() },
      (res) => {
        setMinVersionSaving(false);
        setMinVersionMsg(res.pass ? "Saved." : "Failed to save.");
      }
    );
  };

  const handleClearMinVersion = () => {
    setMinVersionIos("");
    setMinVersionAndroid("");
    setMinVersionWeb("");
    setMinVersionSaving(true);
    setMinVersionMsg("");
    DataBase.updateMinSupportedVersion({ ios: "", android: "", web: "" }, (res) => {
      setMinVersionSaving(false);
      setMinVersionMsg(res.pass ? "Cleared." : "Failed to clear.");
    });
  };

  return (
    <div className="tai-paste-overlay" onClick={onHide}>
      <div className="tai-paste-modal admindash-modal" onClick={(e) => e.stopPropagation()}>
        <div className="admindash-header">
          <p className="tai-paste-title">Admin Dashboard</p>
          <span className="admindash-updated">{loadedAt ? `Updated ${loadedAt.toFormat("HH:mm:ss")}` : ""}</span>
          <button className="tai-cancel-btn" onClick={() => setRefreshKey((k) => k + 1)} disabled={refreshing}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {error !== "" && <p className="tai-paste-error">{error}</p>}

        {loading ? (
          <p className="tai-paste-hint">Loading…</p>
        ) : (
          <>
            <div className="admindash-tabs">
              {TABS.map((t) => (
                <button key={t.key} className={`admindash-tab${tab === t.key ? " admindash-tab-active" : ""}`} onClick={() => setTab(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>

            {(tab === "activity" || tab === "marketing") && (
              <div className="admindash-period-row">
                {PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.days}
                    className={`tai-cancel-btn${periodDays === opt.days ? " admindash-period-active" : ""}`}
                    onClick={() => setPeriodDays(opt.days)}
                  >
                    {opt.label}
                  </button>
                ))}
                <button className="admindash-export" onClick={exportEvents} title="Every event recorded in this period, one row each">
                  Export events (CSV)
                </button>
              </div>
            )}

            {tab === "activity" && (
              <>
              <div className="admindash-totals">
                <div className="admindash-total">
                  <div className="admindash-total-value">{totalUsers}</div>
                  <div className="admindash-total-label">Total Users</div>
                </div>
                <div className="admindash-total">
                  <div className="admindash-total-value">{totalGuides}</div>
                  <div className="admindash-total-label">Total Guides</div>
                </div>
                <div className="admindash-total">
                  <div className="admindash-total-value">{totalShared}</div>
                  <div className="admindash-total-label">Shared Guides</div>
                </div>
                <div className="admindash-total">
                  <div className="admindash-total-value">{uniqueLogins}</div>
                  <div className="admindash-total-label">
                    Unique Logins ({PERIOD_OPTIONS.find((o) => o.days === periodDays)?.label})
                  </div>
                </div>
              </div>

              <BarChart title="New users" counts={usersByDay} days={periodDays} />
              <BarChart title="Total guides" cumulative sortedDates={sortedGuideCreatedDates} days={periodDays} />
              <BarChart title="Guides read (signed-in users)" byPlatform={readsByPlatform} days={periodDays} />
              <BarChart title="Guides written" byPlatform={writesByPlatform} days={periodDays} />
              <BarChart title="Logins" byPlatform={loginsByPlatform} days={periodDays} />
              <BarChart title="New accounts" byPlatform={signupsByPlatform} days={periodDays} />
              </>
            )}

            {tab === "marketing" && (
              <>
              <div className="admindash-lookup admindash-first">
                <p className="tai-paste-title admindash-lookup-title">New visitors on the web — last {periodDays} days</p>
                <p className="admindash-views-note">
                  A new visitor is a browser seen for the first time with nobody signed in. Each step counts how many of them got that far. "Interacted" is three taps after opening a guide. Only people are counted: a visit is recorded at the first tap, key press or mouse move, so automated link checkers (Facebook and Instagram open every posted link) are left out.
                </p>
                {funnel[0].n === 0 ? (
                  <p className="admindash-views-note">No new visitors recorded in this period yet.</p>
                ) : (
                  <div className="admindash-funnel">
                    {funnel.map((step, i) => (
                      <div className="admindash-funnel-row" key={step.label}>
                        <div className="admindash-funnel-label">{step.label}</div>
                        <div className="admindash-funnel-track">
                          {step.n > 0 && <div className="admindash-funnel-fill" style={{ width: `${(step.n / funnel[0].n) * 100}%` }} />}
                        </div>
                        <div className="admindash-funnel-n">{step.n}</div>
                        <div className="admindash-funnel-pct">{i === 0 ? "" : `${Math.round((step.n / funnel[0].n) * 100)}%`}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="tai-check-row" onClick={() => setIncludeAutomated(!includeAutomated)}>
                  <div className={`tai-check-box${includeAutomated ? " checked" : ""}`}>
                    {includeAutomated && <span>✓</span>}
                  </div>
                  <span className="tai-check-label">
                    Include possibly automated visits ({possiblyAutomatedVids.size}) — on a computer with mouse movement only, like Google's link scanner
                  </span>
                </div>
                <p className="admindash-views-note">
                  Returning visitors (seen before, back in this period): {returningVisitors}
                  {returningVisitors > 0 ? ` — ${returningOpened} opened a guide, ${returningInteracted} interacted` : ""}. They are not part of the bars above.
                </p>
              </div>

              <div className="admindash-lookup">
                <p className="tai-paste-title admindash-lookup-title">Tagged links — last {periodDays} days</p>
                <p className="admindash-views-note">
                  Links posted with a source code (…&src=pin, ?src=tt-bio). A visit is one browser session that arrived on the link. "New accounts" counts sign-ups whose first ever visit came from that code, even days later; "Guides made" and "Made a guide" are what those accounts did next (new guides and copies, on web or phone) in the same period.
                </p>
                {taggedRows.length === 0 ? (
                  <p className="admindash-views-note">No visits from tagged links in this period yet.</p>
                ) : (
                  <div className="admindash-views-wrap">
                    <table className="admindash-views">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Visits</th>
                          <th>New visitors</th>
                          <th title="Guides opened on a tagged guide link">Guide views</th>
                          <th>New accounts</th>
                          <th title="New guides and copies made by those new accounts">Guides made</th>
                          <th title="How many of those new accounts made at least one guide">Made a guide</th>
                        </tr>
                      </thead>
                      <tbody>
                        {taggedRows.map((r) => (
                          <tr key={r.src}>
                            <td>{r.src}</td>
                            <td>{r.visits}</td>
                            <td>{r.newVisitors}</td>
                            <td>{r.views}</td>
                            <td>{r.signups}</td>
                            <td>{r.guidesMade}</td>
                            <td>{r.makers}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {taggedRows.length > 0 && (
                  <button className="admindash-views-more" onClick={exportTaggedLinks}>Export this table (CSV)</button>
                )}
              </div>

              <div className="admindash-lookup">
                <p className="tai-paste-title admindash-lookup-title">New accounts by what first brought them — last {periodDays} days</p>
                <p className="admindash-views-note">
                  Web accounts are traced to the first visit of that browser, even when the sign-up came days later. App accounts can't be traced past the store.
                </p>
                {signupSources.length === 0 ? (
                  <p className="admindash-views-note">No new accounts recorded in this period yet.</p>
                ) : (
                  <div className="admindash-views-wrap">
                    <table className="admindash-views">
                      <thead>
                        <tr><th>Source</th><th>New accounts</th></tr>
                      </thead>
                      <tbody>
                        {signupSources.map(([label, n]) => (
                          <tr key={label}><td>{label}</td><td>{n}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {signupFirstGuides.length > 0 && (
                  <p className="admindash-views-note">
                    First guide they saw: {signupFirstGuides.map(([id, n]) => `${guideNameById.get(id) || id} ${n}`).join(" · ")}
                  </p>
                )}
              </div>

              <div className="admindash-lookup">
                <p className="tai-paste-title admindash-lookup-title">Guide views — last {periodDays} days</p>
                <p className="admindash-views-note">
                  Everyone who opened a guide, signed in or not; a guide's own owner is not counted. "By link" means the guide's own link was opened, which is what a video's QR code does. Counting started when this was deployed.
                </p>
                <div className="tai-check-row" onClick={() => setViewsFeaturedOnly(!viewsFeaturedOnly)}>
                  <div className={`tai-check-box${viewsFeaturedOnly ? " checked" : ""}`}>
                    {viewsFeaturedOnly && <span>✓</span>}
                  </div>
                  <span className="tai-check-label">Featured guides only</span>
                </div>
                <BarChart title="Guide views" byPlatform={viewsByPlatform} days={periodDays} />
                {viewRows.length === 0 ? (
                  <p className="admindash-views-note">No views recorded in this period yet.</p>
                ) : (
                  <div className="admindash-views-wrap">
                    <table className="admindash-views">
                      <thead>
                        <tr>
                          <th>Guide</th>
                          <th title="Everyone who opened it">Views</th>
                          <th title="Opened on the guide's own link (QR code or shared link)">By link</th>
                          <th title="Not signed in">Guests</th>
                          <th title="Copies made of this guide">Copied</th>
                          <th title="Trips built from this guide">Built from</th>
                          <th title="The older counter stored on the guide. It only counts signed-in readers, and since October 2026 only the owner and people with Modify access, so it can be lower than Views.">Login reads</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="admindash-views-total">
                          <td>All {viewRows.length} guides</td>
                          <td>{viewTotals.views}</td>
                          <td>{viewTotals.byLink}</td>
                          <td>{viewTotals.guests}</td>
                          <td>{viewTotals.copies}</td>
                          <td>{viewTotals.builds}</td>
                          <td></td>
                        </tr>
                        {(viewsShowAll ? viewRows : viewRows.slice(0, 25)).map((r) => (
                          <tr key={r.id}>
                            <td className="admindash-views-name" onClick={() => { setTab("guides"); handleSelectRecentGuide(r.id); }} title="Look up this guide">
                              {guideNameById.get(r.id) || r.id}
                            </td>
                            <td>{r.views}</td>
                            <td>{r.byLink}</td>
                            <td>{r.guests}</td>
                            <td>{r.copies}</td>
                            <td>{r.builds}</td>
                            <td>{guideTimesRead.get(r.id) ?? ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {viewRows.length > 0 && (
                  <button className="admindash-views-more" onClick={exportGuideViews}>Export this table (CSV)</button>
                )}{" "}
                {viewRows.length > 25 && (
                  <button className="admindash-views-more" onClick={() => setViewsShowAll(!viewsShowAll)}>
                    {viewsShowAll ? "Show top 25" : `Show all ${viewRows.length}`}
                  </button>
                )}
              </div>
              </>
            )}

            {tab === "guides" && (
              <div className="admindash-lookup">
                <p className="tai-paste-title admindash-lookup-title">Look up any guide</p>
                <div className="tai-check-row" onClick={() => setIncludeOwnGuides(!includeOwnGuides)}>
                  <div className={`tai-check-box${includeOwnGuides ? " checked" : ""}`}>
                    {includeOwnGuides && <span>✓</span>}
                  </div>
                  <span className="tai-check-label">Include my own guides in the list below</span>
                </div>
                <div className="admindash-lookup-row">
                  <select
                    className="admindash-lookup-input"
                    value=""
                    onChange={(e) => handleSelectRecentGuide(e.target.value)}
                  >
                    <option value="">Recently modified guides…</option>
                    {recentGuides.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.guide_name} ({g.mod ? g.mod.slice(0, 10) : "?"})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admindash-lookup-row">
                  <input
                    className="admindash-lookup-input"
                    type="text"
                    value={lookupId}
                    onChange={(e) => setLookupId(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
                    placeholder="guide_info id"
                  />
                  <button className="tai-primary-btn" onClick={() => handleLookup()} disabled={lookupLoading}>
                    {lookupLoading ? "Loading…" : "Load"}
                  </button>
                </div>
                {lookupError !== "" && <p className="tai-paste-error">{lookupError}</p>}
                {lookupResult && (
                  <div className="admindash-lookup-result">
                    <div>
                      <strong>{lookupResult.infoDoc.guide_name}</strong> — owner {lookupResult.infoDoc.user_id}
                    </div>
                    <div className="tai-check-row" onClick={handleToggleFeatured}>
                      <div className={`tai-check-box${lookupResult.infoDoc.type === "Featured" ? " checked" : ""}`}>
                        {lookupResult.infoDoc.type === "Featured" && <span>✓</span>}
                      </div>
                      <span className="tai-check-label">{featuredBusy ? "Updating…" : "Featured"}</span>
                    </div>
                    <button className="tai-primary-btn" onClick={handleOpenGuide}>Open This Guide</button>
                  </div>
                )}
              </div>
            )}
            {tab === "guides" && <FeaturedMainSchedulePanel />}
            {tab === "guides" && <FeaturedWikiFixPanel />}
            {tab === "guides" && <FeaturedGuideFixPanel />}
            {tab === "guides" && <TravelerGuidePanel />}

            {tab === "settings" && (
              <>
              <div className="admindash-lookup">
                <p className="tai-paste-title admindash-lookup-title">Announcement</p>
                <p className="tai-paste-hint">
                  Shown once per app load (via the `announce` field in settings/settings) until cleared or changed.
                  Leave every platform unchecked to show it to everyone (the original behavior); check one or more to
                  target only those platforms.
                </p>
                <div className="admindash-lookup-row">
                  {["ios", "android", "web"].map((platform) => (
                    <div key={platform} className="tai-check-row" onClick={() => toggleAnnouncePlatform(platform)}>
                      <div className={`tai-check-box${announcePlatforms.includes(platform) ? " checked" : ""}`}>
                        {announcePlatforms.includes(platform) && <span>✓</span>}
                      </div>
                      <span className="tai-check-label">{platform === "ios" ? "iOS" : platform === "android" ? "Android" : "Web"}</span>
                    </div>
                  ))}
                  <div className="tai-check-row" onClick={() => setAnnounceTestOnly((v) => !v)}>
                    <div className={`tai-check-box${announceTestOnly ? " checked" : ""}`}>
                      {announceTestOnly && <span>✓</span>}
                    </div>
                    <span className="tai-check-label">Test only (dev builds, won't reach real users)</span>
                  </div>
                </div>
                <textarea
                  className="admindash-announce-textarea"
                  rows={4}
                  value={announceText}
                  onChange={(e) => setAnnounceText(e.target.value)}
                  placeholder="HTML shown to all users…"
                />
                <div className="admindash-lookup-row">
                  <button className="tai-primary-btn" onClick={handleSaveAnnounce} disabled={announceSaving}>
                    {announceSaving ? "Saving…" : "Save"}
                  </button>
                  <button className="tai-cancel-btn" onClick={handleClearAnnounce} disabled={announceSaving}>
                    Clear
                  </button>
                  {announceMsg !== "" && <span className="tai-paste-hint">{announceMsg}</span>}
                </div>
              </div>

              <div className="admindash-lookup">
                <p className="tai-paste-title admindash-lookup-title">Minimum App Version</p>
                <p className="tai-paste-hint">
                  A user on a platform below its own version here sees a persistent "please update"/"please refresh"
                  prompt on every launch — unlike Announcement above, it isn't dismissed. Leave a field blank to
                  disable the gate for that platform.
                </p>
                <div className="admindash-lookup-row">
                  <label className="tai-paste-hint">
                    iOS
                    <input
                      type="text"
                      className="admindash-announce-textarea"
                      value={minVersionIos}
                      onChange={(e) => setMinVersionIos(e.target.value)}
                      placeholder="e.g. 2.4.0"
                    />
                  </label>
                  <label className="tai-paste-hint">
                    Android
                    <input
                      type="text"
                      className="admindash-announce-textarea"
                      value={minVersionAndroid}
                      onChange={(e) => setMinVersionAndroid(e.target.value)}
                      placeholder="e.g. 2.4.0"
                    />
                  </label>
                  <label className="tai-paste-hint">
                    Web
                    <input
                      type="text"
                      className="admindash-announce-textarea"
                      value={minVersionWeb}
                      onChange={(e) => setMinVersionWeb(e.target.value)}
                      placeholder="e.g. 1.0.0"
                    />
                  </label>
                </div>
                <div className="admindash-lookup-row">
                  <button className="tai-primary-btn" onClick={handleSaveMinVersion} disabled={minVersionSaving}>
                    {minVersionSaving ? "Saving…" : "Save"}
                  </button>
                  <button className="tai-cancel-btn" onClick={handleClearMinVersion} disabled={minVersionSaving}>
                    Clear
                  </button>
                  {minVersionMsg !== "" && <span className="tai-paste-hint">{minVersionMsg}</span>}
                </div>
              </div>
              </>
            )}

            {tab === "limits" && (
              <UsageLimitsPanel activeUsersByDay={activeUsersByDay} />
            )}

            {tab === "reports" && <GuideReportsPanel />}
          </>
        )}

        <div className="tai-paste-actions">
          <button className="tai-cancel-btn" onClick={onHide}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
